import { useState, useRef, useCallback, useEffect } from 'react'
import { Loader2, CheckCircle2, AlertCircle, RefreshCw, ShieldCheck } from 'lucide-react'
import { useOnboarding, CrossIdentifierData, BusinessDetails } from '../../contexts/OnboardingContext'
import { validateIdentifier } from '../../utils/validation'
import { apiClient } from '../../services/apiClient'

export interface VerificationStepProps {
  onNext: () => void
  onBack: () => void
}

type IdentifierType = 'gstin' | 'pan' | 'udyam' | 'cin'

/**
 * Primary identifier tabs: GSTIN and PAN (linked — one fetches the other)
 * These are shown as tabs for all business types.
 */
const PRIMARY_TABS: { type: IdentifierType; label: string; placeholder: string; hint: string }[] = [
  {
    type: 'gstin',
    label: 'GSTIN',
    placeholder: 'e.g., 27AAPFU0939F1ZV',
    hint: '15-character alphanumeric (2-digit state code + PAN + entity code + checksum)',
  },
  {
    type: 'pan',
    label: 'PAN',
    placeholder: 'e.g., AAPFU0939F',
    hint: '10-character alphanumeric (5 letters + 4 digits + 1 letter)',
  },
]

/**
 * Secondary identifiers: shown as a separate optional input below primary tabs.
 * - CIN: for Private Limited / Public Limited
 * - LLPIN: for LLP (same format as CIN but different label)
 * - Udyam: for Sole Proprietorship
 */
const SECONDARY_IDENTIFIERS: Record<string, { type: IdentifierType; label: string; placeholder: string; hint: string } | null> = {
  sole_proprietorship: {
    type: 'udyam',
    label: 'Udyam Registration Number',
    placeholder: 'e.g., UDYAM-MH-01-0000001',
    hint: 'Format: UDYAM-XX-00-0000000 (optional — helps verify MSME status)',
  },
  partnership: null, // No secondary identifier
  private_limited: {
    type: 'cin',
    label: 'CIN (Corporate Identification Number)',
    placeholder: 'e.g., U72900KA2015PTC082988',
    hint: '21-character alphanumeric — MCA registration number (optional)',
  },
  public_limited: {
    type: 'cin',
    label: 'CIN (Corporate Identification Number)',
    placeholder: 'e.g., L72900KA2010PLC082988',
    hint: '21-character alphanumeric — MCA registration number (optional)',
  },
  llp: {
    type: 'cin',
    label: 'LLPIN (LLP Identification Number)',
    placeholder: 'e.g., AAA-0001',
    hint: 'LLP registration number from MCA (optional)',
  },
}

// Keep full list for backward compatibility with validation
const IDENTIFIER_TABS = [...PRIMARY_TABS, 
  { type: 'udyam' as IdentifierType, label: 'Udyam', placeholder: 'e.g., UDYAM-MH-01-0000001', hint: 'Format: UDYAM-XX-00-0000000' },
  { type: 'cin' as IdentifierType, label: 'CIN', placeholder: 'e.g., U72900KA2015PTC082988', hint: '21-character alphanumeric' },
]

const IDENTIFIER_TYPE_LABELS: Record<string, string> = {
  gstin: 'GSTIN',
  pan: 'PAN',
  udyam: 'Udyam Number',
  cin: 'CIN',
}

const MAX_RETRIES = 3

interface VerifyApiResponse {
  success: true
  data: {
    businessName: string | null
    registeredAddress: string | null
    businessType: string | null
    registrationDate: string | null
    status: string
    rawIdentifier: string
    fieldsPopulated: string[]
    fieldsMissing: string[]
    crossIdentifiers?: CrossIdentifierData
    businessDetails?: BusinessDetails | null
  }
}

/**
 * VerificationStep handles business identifier input with tabbed type selection,
 * format validation, API verification, cross-identifier display, and confirmation flow.
 */
export function VerificationStep({ onNext, onBack }: VerificationStepProps) {
  const { state, dispatch } = useOnboarding()

  // Primary tabs are always GSTIN and PAN
  const filteredTabs = PRIMARY_TABS
  
  // Secondary identifier based on business type (optional field shown separately)
  const secondaryIdentifier = SECONDARY_IDENTIFIERS[state.businessType] || null

  // Local UI state
  const [selectedType, setSelectedType] = useState<IdentifierType>(
    (state.identifierType as IdentifierType) || 'gstin'
  )
  const [inputValue, setInputValue] = useState(state.identifierValue || '')
  const [secondaryValue, setSecondaryValue] = useState('')
  const [validationError, setValidationError] = useState<string | null>(null)
  const [businessDetails, setBusinessDetails] = useState<BusinessDetails | null>(
    state.verifiedBusinessDetails || null
  )

  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Derived state
  const isLoading = state.verificationStatus === 'loading'
  const isVerified = state.verificationStatus === 'success'
  const hasError = state.verificationStatus === 'error'
  const retriesExhausted = state.retryCount >= MAX_RETRIES

  const activeTab = filteredTabs.find((t) => t.type === selectedType) || filteredTabs[0]

  // Clear debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [])

  /**
   * Handle tab switch — clear input and errors when switching identifier type.
   */
  const handleTabChange = (type: IdentifierType) => {
    if (type === selectedType) return
    setSelectedType(type)
    setInputValue('')
    setValidationError(null)
    dispatch({
      type: 'SET_IDENTIFIER',
      payload: { identifierType: type, identifierValue: '' },
    })
    // Reset verification state if switching tabs
    if (state.verificationStatus !== 'idle') {
      dispatch({
        type: 'SET_VERIFICATION_RESULT',
        payload: { status: 'idle', error: null },
      })
    }
    // Focus the input after tab switch
    setTimeout(() => inputRef.current?.focus(), 50)
  }

  /**
   * Validates the identifier format. Called on blur or after 500ms debounce.
   */
  const runValidation = useCallback(
    (value: string) => {
      const upperValue = value.toUpperCase()
      const error = validateIdentifier(upperValue, selectedType)
      setValidationError(error)

      // Update context
      dispatch({
        type: 'SET_IDENTIFIER',
        payload: {
          identifierType: selectedType,
          identifierValue: upperValue,
        },
      })

      return error === null
    },
    [dispatch, selectedType]
  )

  /**
   * Handle input change — no validation errors while typing,
   * but start a 500ms debounce timer for validation.
   */
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase()
    setInputValue(value)
    setValidationError(null)

    // Update context with current value
    dispatch({
      type: 'SET_IDENTIFIER',
      payload: {
        identifierType: selectedType,
        identifierValue: value,
      },
    })

    // Clear previous debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    // Start 500ms debounce for validation
    if (value.length > 0) {
      debounceTimerRef.current = setTimeout(() => {
        runValidation(value)
      }, 500)
    }
  }

  /**
   * Validate on blur (immediate validation trigger).
   */
  const handleBlur = () => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
      debounceTimerRef.current = null
    }
    if (inputValue.length > 0) {
      runValidation(inputValue)
    }
  }

  /**
   * Submit identifier for verification via API.
   */
  const handleVerify = async () => {
    // Run validation first
    const isValid = runValidation(inputValue)
    if (!isValid) return

    if (!state.businessType) return

    // Set loading state
    dispatch({
      type: 'SET_VERIFICATION_RESULT',
      payload: { status: 'loading', error: null },
    })

    try {
      const response = await apiClient.post<VerifyApiResponse>('/kyb/verify', {
        identifierType: selectedType,
        identifierValue: inputValue.toUpperCase(),
        businessType: state.businessType,
      })

      const data = response.data.data

      // Set success state with business details
      dispatch({
        type: 'SET_VERIFICATION_RESULT',
        payload: {
          status: 'success',
          businessName: data.businessName,
          address: data.registeredAddress,
          error: null,
          businessDetails: data.businessDetails || null,
        },
      })

      // Set cross-identifiers if available
      if (data.crossIdentifiers) {
        dispatch({
          type: 'SET_CROSS_IDENTIFIERS',
          payload: data.crossIdentifiers,
        })
      }

      // Store business details for display
      if (data.businessDetails) {
        setBusinessDetails(data.businessDetails)
        
        // Pre-fill user name with the person's name from verification:
        // - Sole proprietors: legalName = person's name (trade name = business)
        // - Others: use legalName as a suggestion if user hasn't typed one yet
        if (data.businessDetails.legalName) {
          const currentName = state.userName?.trim()
          if (state.businessType === 'sole_proprietorship' || !currentName) {
            dispatch({
              type: 'SET_USER_DETAILS',
              payload: { userName: data.businessDetails.legalName },
            })
          }
        }
      }
    } catch (err: unknown) {
      // Increment retry count
      dispatch({ type: 'INCREMENT_RETRY' })

      // Extract error message from the API error response
      let errorMessage = 'Verification failed. Please try again.'
      let errorCode = ''
      
      if (err && typeof err === 'object') {
        // The apiClient interceptor transforms errors to { message, code, details }
        const apiErr = err as { message?: string; code?: string; details?: string }
        errorCode = apiErr.code || ''
        
        switch (errorCode) {
          case 'IDENTIFIER_NOT_FOUND':
            errorMessage =
              "We couldn't find a business with this identifier. Please check and try again."
            break
          case 'SERVICE_UNAVAILABLE':
            errorMessage =
              'Business verification service is temporarily unavailable. Please try again in a few minutes.'
            break
          case 'TIMEOUT':
            errorMessage =
              'Verification is taking longer than expected. Please try again.'
            break
          case 'INVALID_FORMAT':
            errorMessage =
              'The identifier format is invalid. Please check and correct it.'
            break
          default:
            errorMessage = apiErr.message || errorMessage
            break
        }
      }

      dispatch({
        type: 'SET_VERIFICATION_RESULT',
        payload: { status: 'error', error: errorMessage },
      })
    }
  }

  /**
   * Handle retry — reset error state and allow re-submission.
   */
  const handleRetry = () => {
    dispatch({
      type: 'SET_VERIFICATION_RESULT',
      payload: { status: 'idle', error: null },
    })
  }

  /**
   * Handle confirmation of fetched identifiers and proceed.
   */
  const handleConfirmAndProceed = () => {
    onNext()
  }

  /**
   * Handle changing identifier — reset verification state to allow re-entry.
   */
  const handleChangeIdentifier = () => {
    setInputValue('')
    setValidationError(null)
    setBusinessDetails(null)
    dispatch({
      type: 'SET_VERIFICATION_RESULT',
      payload: { status: 'idle', error: null },
    })
    dispatch({
      type: 'SET_IDENTIFIER',
      payload: { identifierType: selectedType, identifierValue: '' },
    })
    dispatch({
      type: 'SET_CROSS_IDENTIFIERS',
      payload: { pan: null, gstinList: null, udyamList: null, fetchedFrom: selectedType },
    })
    // Reset retry count by dispatching a reset-like action
    // We'll just reset the verification status which is enough
    setTimeout(() => inputRef.current?.focus(), 50)
  }

  return (
    <div className="w-full max-w-2xl mx-auto">
      <h2 className="text-2xl font-semibold text-secondary-900 mb-2">
        Verify your business
      </h2>
      <p className="text-secondary-500 mb-6">
        Select your identifier type and enter the value to verify your business details.
      </p>

      {/* Identifier input section */}
      {!isVerified && (
        <div className="space-y-4">
          {/* Tabs for identifier type selection */}
          <div className="border-b border-secondary-200">
            <nav className="flex -mb-px" aria-label="Identifier type">
              {filteredTabs.map((tab) => (
                <button
                  key={tab.type}
                  type="button"
                  onClick={() => handleTabChange(tab.type)}
                  disabled={isLoading || retriesExhausted}
                  className={`
                    flex-1 py-3 px-4 text-center text-sm font-medium border-b-2 transition-colors
                    focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-inset
                    disabled:opacity-50 disabled:cursor-not-allowed
                    ${
                      selectedType === tab.type
                        ? 'border-primary-600 text-primary-600 bg-primary-50/50'
                        : 'border-transparent text-secondary-500 hover:text-secondary-700 hover:border-secondary-300'
                    }
                  `}
                  aria-selected={selectedType === tab.type}
                  role="tab"
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Input field */}
          <div>
            <label
              htmlFor="identifier-input"
              className="block text-sm font-medium text-secondary-700 mb-1"
            >
              {activeTab.label} Number
            </label>
            <input
              ref={inputRef}
              id="identifier-input"
              type="text"
              value={inputValue}
              onChange={handleInputChange}
              onBlur={handleBlur}
              placeholder={activeTab.placeholder}
              disabled={isLoading || retriesExhausted}
              aria-describedby={validationError ? 'identifier-error' : 'identifier-hint'}
              aria-invalid={!!validationError}
              className={`
                w-full px-4 py-3 rounded-lg border text-sm font-mono uppercase text-gray-900
                focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1
                disabled:bg-secondary-100 disabled:cursor-not-allowed
                ${
                  validationError
                    ? 'border-red-400 bg-red-50'
                    : 'border-secondary-300 bg-white'
                }
              `}
            />

            {/* Format hint */}
            {!validationError && (
              <p id="identifier-hint" className="mt-1.5 text-xs text-secondary-400">
                {activeTab.hint}
              </p>
            )}

            {/* Inline validation error */}
            {validationError && (
              <p
                id="identifier-error"
                role="alert"
                className="mt-1.5 text-sm text-red-600 flex items-center gap-1"
              >
                <AlertCircle className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
                {validationError}
              </p>
            )}
          </div>

          {/* Secondary identifier field — shown alongside primary input */}
          {secondaryIdentifier && (
            <div className="p-4 rounded-lg border border-dashed border-secondary-300 bg-secondary-50/50">
              <label className="block text-sm font-medium text-secondary-700 mb-1.5">
                {secondaryIdentifier.label}
                <span className="ml-1.5 text-xs text-secondary-400 font-normal">(optional)</span>
              </label>
              <input
                type="text"
                value={secondaryValue}
                onChange={(e) => {
                  const val = e.target.value.toUpperCase()
                  setSecondaryValue(val)
                  dispatch({
                    type: 'SET_SECONDARY_IDENTIFIER',
                    payload: {
                      secondaryIdentifierType: secondaryIdentifier.type as 'cin' | 'udyam',
                      secondaryIdentifierValue: val,
                    },
                  })
                }}
                placeholder={secondaryIdentifier.placeholder}
                disabled={isLoading || retriesExhausted}
                className="w-full px-4 py-2.5 rounded-lg border border-secondary-300 bg-white text-secondary-900 placeholder-secondary-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-mono text-sm tracking-wider uppercase disabled:bg-secondary-100 disabled:cursor-not-allowed"
              />
              <p className="mt-1.5 text-xs text-secondary-400">
                {secondaryIdentifier.hint}
              </p>
            </div>
          )}

          {/* Verification error message */}
          {hasError && state.verificationError && (
            <div
              role="alert"
              className="p-4 rounded-lg bg-red-50 border border-red-200"
            >
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" aria-hidden="true" />
                <div className="flex-1">
                  <p className="text-sm text-red-700">{state.verificationError}</p>
                  {!retriesExhausted && (
                    <p className="text-xs text-red-500 mt-1">
                      Attempt {state.retryCount} of {MAX_RETRIES}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Retries exhausted message */}
          {retriesExhausted && (
            <div
              role="alert"
              className="p-4 rounded-lg bg-amber-50 border border-amber-200"
            >
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
                <div>
                  <p className="text-sm font-medium text-amber-800">
                    Verification attempts exhausted
                  </p>
                  <p className="text-sm text-amber-700 mt-1">
                    We&apos;re having trouble verifying your business. Please contact support at{' '}
                    <a
                      href="mailto:support@tlbh.com"
                      className="underline font-medium"
                    >
                      support@tlbh.com
                    </a>
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Action buttons */}
          <div className="flex items-center justify-between pt-2">
            <button
              type="button"
              onClick={onBack}
              className="px-4 py-2.5 rounded-lg text-sm font-medium text-secondary-600 hover:text-secondary-800 hover:bg-secondary-100 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            >
              Back
            </button>

            <div className="flex items-center gap-3">
              {/* Retry button */}
              {hasError && !retriesExhausted && (
                <button
                  type="button"
                  onClick={handleRetry}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-primary-600 border border-primary-300 hover:bg-primary-50 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                >
                  <RefreshCw className="w-4 h-4" aria-hidden="true" />
                  Retry
                </button>
              )}

              {/* Verify button */}
              <button
                type="button"
                onClick={handleVerify}
                disabled={isLoading || retriesExhausted || !inputValue.trim() || !!validationError}
                className={`
                  flex items-center gap-2 px-6 py-2.5 rounded-lg text-sm font-medium transition-colors
                  focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
                  ${
                    isLoading || retriesExhausted || !inputValue.trim() || !!validationError
                      ? 'bg-secondary-200 text-secondary-400 cursor-not-allowed'
                      : 'bg-primary-600 text-white hover:bg-primary-700'
                  }
                `}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                    Verifying...
                  </>
                ) : (
                  'Verify'
                )}
              </button>
            </div>
          </div>

          {/* Loading indicator text */}
          {isLoading && (
            <p className="text-sm text-secondary-500 text-center" aria-live="polite">
              Verifying your business identifier. This may take a few seconds...
            </p>
          )}
        </div>
      )}

      {/* Success: Auto-populated fields and cross-identifier confirmation */}
      {isVerified && (
        <div className="space-y-6">
          {/* Verified identifier badge */}
          <div className="flex items-center gap-2 p-3 rounded-lg bg-success-50 border border-success-200">
            <CheckCircle2 className="w-5 h-5 text-success-600" aria-hidden="true" />
            <span className="text-sm font-medium text-success-700">
              {IDENTIFIER_TYPE_LABELS[state.identifierType || ''] || 'Identifier'} verified successfully
            </span>
            <span className="ml-auto text-xs font-mono text-success-600">
              {state.identifierValue}
            </span>
            <button
              type="button"
              onClick={handleChangeIdentifier}
              className="ml-2 text-xs text-primary-600 hover:text-primary-800 underline font-medium"
            >
              Change
            </button>
          </div>

          {/* Auto-populated business details */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-secondary-800">
              Business Details
              <span className="ml-2 text-xs font-normal text-secondary-500">
                (auto-populated from verification)
              </span>
            </h3>

            {state.verifiedBusinessName && (
              <div className="p-3 rounded-lg bg-primary-50 border border-primary-100">
                <p className="text-xs text-secondary-500 mb-0.5">Business Name</p>
                <p className="text-sm font-medium text-secondary-900">
                  {state.verifiedBusinessName}
                </p>
                <span className="inline-flex items-center mt-1 text-xs text-primary-600">
                  <CheckCircle2 className="w-3 h-3 mr-1" aria-hidden="true" />
                  Auto-populated
                </span>
              </div>
            )}

            {state.verifiedAddress && (
              <div className="p-3 rounded-lg bg-primary-50 border border-primary-100">
                <p className="text-xs text-secondary-500 mb-0.5">Registered Address</p>
                <p className="text-sm font-medium text-secondary-900">
                  {state.verifiedAddress}
                </p>
                <span className="inline-flex items-center mt-1 text-xs text-primary-600">
                  <CheckCircle2 className="w-3 h-3 mr-1" aria-hidden="true" />
                  Auto-populated
                </span>
              </div>
            )}
          </div>

          {/* Optional: Add more identifiers when CIN was used (no cross-identifiers fetched) */}
          {selectedType === 'cin' && (!state.crossIdentifiers || !state.crossIdentifiers.gstinList || state.crossIdentifiers.gstinList.length === 0) && (
            <div className="space-y-3 p-4 rounded-lg bg-secondary-50 border border-secondary-200">
              <p className="text-sm text-secondary-700 font-medium">
                Want to fetch more details? Enter your GSTIN or PAN below (optional)
              </p>
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Enter GSTIN or PAN (optional)"
                  className="flex-1 px-3 py-2 rounded-lg border border-secondary-300 bg-white text-sm font-mono uppercase text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      const target = e.target as HTMLInputElement
                      const val = target.value.trim().toUpperCase()
                      if (val) {
                        // Switch to the appropriate tab and verify
                        if (val.length === 15) {
                          setSelectedType('gstin')
                        } else if (val.length === 10) {
                          setSelectedType('pan')
                        }
                        setInputValue(val)
                        dispatch({ type: 'SET_VERIFICATION_RESULT', payload: { status: 'idle', error: null } })
                      }
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={() => {
                    // Just proceed without additional identifiers
                  }}
                  className="px-3 py-2 text-xs text-secondary-500 hover:text-secondary-700"
                >
                  Skip
                </button>
              </div>
              <p className="text-xs text-secondary-400">
                Press Enter to verify, or skip to continue with just the company name.
              </p>
            </div>
          )}

          {/* Cross-identifier confirmation section */}
          {state.crossIdentifiers && state.crossIdentifiers.gstinList && state.crossIdentifiers.gstinList.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-primary-600" aria-hidden="true" />
                <h3 className="text-sm font-semibold text-secondary-800">
                  Linked Identifiers
                </h3>
              </div>
              <p className="text-xs text-secondary-500">
                The following identifiers were found linked to your business. Please review and confirm.
              </p>

              <div className="rounded-lg border border-secondary-200 divide-y divide-secondary-100 overflow-hidden">
                {/* PAN */}
                {state.crossIdentifiers.pan && (
                  <div className="p-3 bg-white">
                    <p className="text-xs text-secondary-500 mb-0.5">PAN</p>
                    <p className="text-sm font-mono font-medium text-secondary-900">
                      {state.crossIdentifiers.pan}
                    </p>
                  </div>
                )}

                {/* GSTIN(s) */}
                {state.crossIdentifiers.gstinList &&
                  state.crossIdentifiers.gstinList.length > 0 && (
                    <div className="p-3 bg-white">
                      <p className="text-xs text-secondary-500 mb-1">
                        GSTIN{state.crossIdentifiers.gstinList.length > 1 ? 's' : ''}
                      </p>
                      <div className="space-y-2">
                        {state.crossIdentifiers.gstinList.map((gstinInfo) => (
                          <div key={gstinInfo.gstin} className="flex items-center justify-between">
                            <p className="text-sm font-mono font-medium text-secondary-900">
                              {gstinInfo.gstin}
                            </p>
                            <div className="flex items-center gap-2">
                              {gstinInfo.state && (
                                <span className="text-xs px-1.5 py-0.5 rounded bg-secondary-100 text-secondary-600">
                                  {gstinInfo.state}
                                </span>
                              )}
                              <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                                gstinInfo.status === 'ACTIVE' 
                                  ? 'bg-green-100 text-green-700' 
                                  : 'bg-amber-100 text-amber-700'
                              }`}>
                                {gstinInfo.status}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                {/* Udyam number(s) */}
                {state.crossIdentifiers.udyamList &&
                  state.crossIdentifiers.udyamList.length > 0 && (
                    <div className="p-3 bg-white">
                      <p className="text-xs text-secondary-500 mb-0.5">
                        Udyam Number{state.crossIdentifiers.udyamList.length > 1 ? 's' : ''}
                      </p>
                      <div className="space-y-1">
                        {state.crossIdentifiers.udyamList.map((udyam) => (
                          <p
                            key={udyam}
                            className="text-sm font-mono font-medium text-secondary-900"
                          >
                            {udyam}
                          </p>
                        ))}
                      </div>
                    </div>
                  )}

                {/* Fetched from indicator */}
                <div className="p-2 bg-secondary-50">
                  <p className="text-xs text-secondary-400 text-center">
                    Fetched from {IDENTIFIER_TYPE_LABELS[state.crossIdentifiers.fetchedFrom]} verification
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Rich Business Details (from GSTIN verification) */}
          {businessDetails && (
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-secondary-800">
                Additional Business Information
              </h3>
              <div className="rounded-lg border border-secondary-200 overflow-hidden">
                <div className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-secondary-100">
                  {businessDetails.constitutionOfBusiness && (
                    <div className="p-3">
                      <p className="text-xs text-secondary-500">Constitution</p>
                      <p className="text-sm font-medium text-secondary-900">{businessDetails.constitutionOfBusiness}</p>
                    </div>
                  )}
                  {businessDetails.taxpayerType && (
                    <div className="p-3">
                      <p className="text-xs text-secondary-500">Taxpayer Type</p>
                      <p className="text-sm font-medium text-secondary-900">{businessDetails.taxpayerType}</p>
                    </div>
                  )}
                </div>
                {businessDetails.tradeName && (
                  <div className="p-3 border-t border-secondary-100">
                    <p className="text-xs text-secondary-500">Trade Name</p>
                    <p className="text-sm font-medium text-secondary-900">{businessDetails.tradeName}</p>
                  </div>
                )}
                {businessDetails.natureOfBusinessActivities && businessDetails.natureOfBusinessActivities.length > 0 && (
                  <div className="p-3 border-t border-secondary-100">
                    <p className="text-xs text-secondary-500 mb-1">Nature of Business</p>
                    <div className="flex flex-wrap gap-1">
                      {businessDetails.natureOfBusinessActivities.map((activity) => (
                        <span key={activity} className="text-xs px-2 py-0.5 rounded-full bg-primary-50 text-primary-700 border border-primary-100">
                          {activity}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {businessDetails.principalPlaceSplitAddress && (
                  <div className="p-3 border-t border-secondary-100">
                    <p className="text-xs text-secondary-500 mb-1">Address Details</p>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      {businessDetails.principalPlaceSplitAddress.city && (
                        <div>
                          <span className="text-secondary-400">City: </span>
                          <span className="text-secondary-800">{businessDetails.principalPlaceSplitAddress.city}</span>
                        </div>
                      )}
                      {businessDetails.principalPlaceSplitAddress.district && (
                        <div>
                          <span className="text-secondary-400">District: </span>
                          <span className="text-secondary-800">{businessDetails.principalPlaceSplitAddress.district}</span>
                        </div>
                      )}
                      {businessDetails.principalPlaceSplitAddress.state && (
                        <div>
                          <span className="text-secondary-400">State: </span>
                          <span className="text-secondary-800">{businessDetails.principalPlaceSplitAddress.state}</span>
                        </div>
                      )}
                      {businessDetails.principalPlaceSplitAddress.pincode && (
                        <div>
                          <span className="text-secondary-400">Pincode: </span>
                          <span className="text-secondary-800">{businessDetails.principalPlaceSplitAddress.pincode}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-2 divide-x divide-secondary-100 border-t border-secondary-100">
                  {businessDetails.dateOfRegistration && (
                    <div className="p-3">
                      <p className="text-xs text-secondary-500">Registration Date</p>
                      <p className="text-sm font-medium text-secondary-900">{businessDetails.dateOfRegistration}</p>
                    </div>
                  )}
                  {businessDetails.lastUpdateDate && (
                    <div className="p-3">
                      <p className="text-xs text-secondary-500">Last Updated</p>
                      <p className="text-sm font-medium text-secondary-900">{businessDetails.lastUpdateDate}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Confirm and proceed buttons */}
          <div className="flex items-center justify-between pt-4 border-t border-secondary-200">
            <button
              type="button"
              onClick={onBack}
              className="px-4 py-2.5 rounded-lg text-sm font-medium text-secondary-600 hover:text-secondary-800 hover:bg-secondary-100 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            >
              Back
            </button>

            <button
              type="button"
              onClick={handleConfirmAndProceed}
              className="flex items-center gap-2 px-6 py-2.5 rounded-lg text-sm font-medium bg-primary-600 text-white hover:bg-primary-700 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            >
              <CheckCircle2 className="w-4 h-4" aria-hidden="true" />
              Confirm &amp; Continue
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
